import { MAX_WEBVIEW_AUTH_RECOVERIES } from './webview-recovery-pacing.logic';

/**
 * Pure decision logic for what to do when native answers with an AUTH_TOKEN.
 *
 * Extracted so it can be unit-tested by the repo's node-env runner, which only picks up
 * `*.logic.test.ts` — the navigation itself touches window.location and cannot run there.
 */

/**
 * Max AUTH_TOKEN-driven document loads per WebView browsing context.
 *
 * DERIVED, not an independent magic number. Every request that can be answered may
 * legitimately want to navigate: `use-webview-auth` sends one on mount, and recovery sends
 * MAX_WEBVIEW_AUTH_RECOVERIES more. Hardcoding 2 here silently discarded the LAST
 * recovery's reply as budget-exhausted — the final re-auth chance thrown away by an
 * off-by-one between two constants nothing tied together. Same defect class the retry
 * timeout already avoids by deriving from the reply window.
 */
export const MAX_AUTH_REPLY_NAVIGATIONS = MAX_WEBVIEW_AUTH_RECOVERIES + 1;

export interface ReplySourceCheck {
  /**
   * `event.source === window` — the document posted to itself. `null` when `source` is
   * unavailable, which is not the same as "false".
   */
  sourceIsSelf: boolean | null;
  /** `event.origin`, verbatim. May be '' or the literal 'null' for an opaque origin. */
  origin: string;
  /** `window.location.origin`. */
  ownOrigin: string;
}

/**
 * May this `message` event be treated as native's reply?
 *
 * Native injects `window.postMessage(json)` into the top frame, so a genuine reply is this
 * document posting to itself. The listener is app-wide, so without this check any embedded
 * frame could post an AUTH_TOKEN and have its token adopted — adoption compares only `exp`,
 * which the sender controls — and its `route` handed to `location.replace`.
 *
 * Rejects only what it can POSITIVELY attribute elsewhere, and allows anything
 * unattributable. That asymmetry is deliberate: a false reject drops a real reply, which
 * reproduces the blank screen this branch exists to remove, and that is a far worse outcome
 * than the hazard being closed. An opaque origin ('' or 'null') is therefore not disqualifying
 * on its own — `sourceIsSelf` is the reliable discriminator, since a frame posting to its
 * parent sets `source` to that frame's window.
 */
export const isTrustedReplySource = ({
  sourceIsSelf,
  origin,
  ownOrigin,
}: ReplySourceCheck): boolean => {
  if (sourceIsSelf === false) return false;
  if (origin && origin !== 'null' && origin !== ownOrigin) return false;
  return true;
};

export interface AuthReplyNavigation {
  /** Perform a full document load. */
  navigate: boolean;
  /** Absolute path to load. Meaningless when `navigate` is false. */
  url: string;
  /** Why navigation was suppressed, for logging. Empty when navigating. */
  reason: '' | 'token-unusable' | 'budget-exhausted' | 'offline';
}

export interface AuthReplyInput {
  /** `route` field from native's reply. Untyped: native does not always send it. */
  route: unknown;
  currentPath: string;
  search: string;
  /**
   * The reply's token is USABLE — live, datable, worth acting on. Deliberately NOT
   * "we adopted it": adoption additionally requires the token to be an UPGRADE, and native
   * syncing the same token CF already holds is the normal mid-session case. Gating
   * navigation on adoption would therefore suppress the route native asked us to open
   * whenever the tokens merely match, which is most of the time.
   */
  tokenUsable: boolean;
  /** AUTH_TOKEN-driven document loads already performed in this browsing context. */
  navigationsUsed: number;
  /**
   * The browser reports no network connection (`navigator.onLine === false`).
   *
   * Present so a reply can never start a document load we know will fail. See hazard 4.
   */
  isOffline: boolean;
}

/**
 * Decide where — and whether — to navigate on an AUTH_TOKEN reply.
 *
 * Three hazards:
 *
 * 1. **Missing route → `/undefined`.** Native does not always include `route`, so fall
 *    back to the CURRENT path. The reply also arrives mid-session for already-authed users
 *    on the recovery path, where bouncing someone off their screen is itself a defect.
 *    (Only that path: Http's 401 retry sends REQUEST_TOKEN_REFRESH, which native answers
 *    with `AUTH_TOKEN_REFRESHED` — a type this listener's bridge does not convert, so it
 *    cannot navigate at all.)
 *
 * 2. **An UNUSABLE token must never trigger a reload.** Reloading onto a token that
 *    is expired or undatable leaves the fresh document initialising `isLoggedIn` from
 *    `!isExpired()` → false, i.e. the login form, having spent a navigation to get there.
 *    Note this is USABILITY, not adoption: a token equal to CF's own is not adopted (it is
 *    no upgrade) but is perfectly fine to navigate on. Worse, before the adoption check existed the reply also
 *    OVERWROTE CF's token, so each cycle re-armed itself: the fresh document had
 *    `!isLoggedIn`, asked again, and looped with no terminal state. The recovery counter
 *    could not bound that, because its timer needs 8s of continuous mount while a
 *    fast-failing refresh reloads in ~1-2s.
 *
 * 3. **A late duplicate reply reloading again.** Native's reply is `{ type, token, route }`
 *    and never echoes the nonce `requestAuthFromNative` mints, so a reply cannot be matched
 *    to its request. The listener is app-wide and re-registers on every document, so a
 *    reply to a superseded request lands on the freshly loaded page and navigates again,
 *    discarding whatever the user had started.
 *
 *    The bound must be DURABLE across documents, because `replace()` starts a new document and
 *    resets module state — hence sessionStorage. It is NEVER reset: settle, a successful
 *    refresh and a new session all happen inside the newly loaded document, i.e. precisely
 *    while a late duplicate may still be in flight, so every candidate edge lets the reply
 *    that caused the load clear the bound one hop later. See WebViewAuthService.navigations for
 *    what exhausting it does and does not cost.
 *
 * 4. **Reloading while OFFLINE.** A document load with no connection lands on the native
 *    "webpage not available" page, which in a WebView with no address bar is exactly the
 *    unrecoverable state this branch exists to remove. shouldAttemptWebviewAuthRecovery already
 *    refuses to reload while offline, but that guard is on the REQUEST side and the hazard is on
 *    the REPLY side — so it did not cover the terminal screen's manual Retry, which is
 *    deliberately tappable while offline. Native can answer without a working network of its own
 *    (`sendAuthToken` sends the existing token when its refresh fails, mobile-app
 *    authService.ts), so a usable token could come back and navigate straight into the error
 *    page. Guarding here covers the auto and manual paths with one check, and costs nothing: the
 *    token is still adopted by the callback, which runs before this decision.
 */
export const planAuthReplyNavigation = ({
  route,
  currentPath,
  search,
  tokenUsable,
  navigationsUsed,
  isOffline,
}: AuthReplyInput): AuthReplyNavigation => {
  const target = typeof route === 'string' && route.length > 0 ? route : currentPath;
  const url = target + search;

  if (!tokenUsable) return { navigate: false, url, reason: 'token-unusable' };
  if (isOffline) return { navigate: false, url, reason: 'offline' };
  if (navigationsUsed >= MAX_AUTH_REPLY_NAVIGATIONS) {
    return { navigate: false, url, reason: 'budget-exhausted' };
  }
  return { navigate: true, url, reason: '' };
};
